import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { SsoAdminService, type SsoConfig } from './SsoAdminService';

export interface ProviderTemplateField {
    key: string;
    label: string;
    placeholder?: string;
    secret?: boolean;
    required?: boolean;
}

export interface ProviderTemplate {
    value: string;
    label: string;
    fields: ProviderTemplateField[];
    extrasJson?: string;
}

export const PROVIDER_TEMPLATES: ProviderTemplate[] = [
    {
        value: 'google',
        label: 'Google Workspace',
        fields: [
            { key: 'clientId', label: 'Client ID', placeholder: 'xxx.apps.googleusercontent.com', required: true },
            { key: 'clientSecret', label: 'Client Secret', secret: true, required: true },
            { key: 'hostedDomain', label: 'Hosted Domain', placeholder: 'acme.com (restricts to this domain)' },
        ],
    },
    {
        value: 'oidc',
        label: 'Generic OIDC',
        fields: [
            { key: 'authorizationUrl', label: 'Authorization URL', required: true },
            { key: 'tokenUrl', label: 'Token URL', required: true },
            { key: 'userInfoUrl', label: 'User Info URL', required: true },
            { key: 'clientId', label: 'Client ID', required: true },
            { key: 'clientSecret', label: 'Client Secret', secret: true, required: true },
            { key: 'defaultScope', label: 'Scopes', placeholder: 'openid email profile' },
        ],
    },
    {
        value: 'saml',
        label: 'SAML 2.0',
        fields: [
            { key: 'singleSignOnServiceUrl', label: 'SSO Service URL', required: true },
            { key: 'entityId', label: 'Entity ID', required: true },
            { key: 'signingCertificate', label: 'Signing Certificate (PEM)', required: true },
        ],
    },
];

interface FormState {
    alias: string;
    providerId: string;
    displayName: string;
    enabled: boolean;
    config: Record<string, string>;
}

const EMPTY_FORM: FormState = {
    alias: '',
    providerId: 'google',
    displayName: '',
    enabled: true,
    config: {},
};

@injectable()
export class SsoPageViewModel extends BaseViewModel {
    configs: SsoConfig[] | null = null;
    loading: boolean = true;

    modalOpen: boolean = false;
    editing: boolean = false;
    form: FormState = { ...EMPTY_FORM, config: {} };
    saving: boolean = false;

    constructor(private service: SsoAdminService) {
        super();
    }

    get template(): ProviderTemplate {
        return PROVIDER_TEMPLATES.find((p) => p.value === this.form.providerId) ?? PROVIDER_TEMPLATES[0]!;
    }

    async load(): Promise<void> {
        this.loading = true;
        this.notifyChange();
        try {
            const data = await this.service.listConfigs();
            this.configs = data;
        } catch {
            this.configs = null;
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    openCreate(): void {
        this.form = { ...EMPTY_FORM, config: {} };
        this.editing = false;
        this.modalOpen = true;
        this.notifyChange();
    }

    openEdit(cfg: SsoConfig): void {
        this.form = {
            alias: cfg.alias,
            providerId: cfg.providerId,
            displayName: cfg.displayName,
            enabled: cfg.enabled,
            config: { ...cfg.config },
        };
        this.editing = true;
        this.modalOpen = true;
        this.notifyChange();
    }

    closeModal(): void {
        this.modalOpen = false;
        this.notifyChange();
    }

    setProvider(value: string): void {
        this.form = { ...this.form, providerId: value, config: {} };
        this.notifyChange();
    }

    setAlias(value: string): void {
        this.form = { ...this.form, alias: value };
        this.notifyChange();
    }

    setDisplayName(value: string): void {
        this.form = { ...this.form, displayName: value };
        this.notifyChange();
    }

    setEnabled(value: boolean): void {
        this.form = { ...this.form, enabled: value };
        this.notifyChange();
    }

    setConfigField(key: string, value: string): void {
        this.form = { ...this.form, config: { ...this.form.config, [key]: value } };
        this.notifyChange();
    }

    async save(): Promise<{ success: boolean; editing: boolean; displayName: string; error?: string }> {
        if (!this.form.alias || !this.form.displayName) {
            return { success: false, editing: this.editing, displayName: this.form.displayName, error: 'Alias and Display Name are required' };
        }
        this.saving = true;
        this.notifyChange();
        const wasEditing = this.editing;
        const displayName = this.form.displayName;
        try {
            await this.service.saveConfig(this.form);
            this.form = { ...EMPTY_FORM, config: {} };
            this.modalOpen = false;
            void this.load();
            return { success: true, editing: wasEditing, displayName };
        } catch (e) {
            return { success: false, editing: wasEditing, displayName, error: (e as Error).message };
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    async deleteConfig(alias: string): Promise<{ success: boolean; error?: string }> {
        try {
            await this.service.deleteConfig(alias);
            void this.load();
            return { success: true };
        } catch (e) {
            return { success: false, error: (e as Error).message };
        }
    }

}
